import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { siteImages, CONTACT_INFO, RETURN_ADDRESS } from '../data/mock';
import { Shield, CheckCircle, RotateCcw, Clock, ArrowRight, Mail, Phone, Award, Heart, Zap } from 'lucide-react';

const GuaranteePage = () => {
  useEffect(() => {
    document.title = 'Our Guarantee | Keto Vybe';
  }, []);

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-20">
        {/* Banner */}
        <section className="relative h-72 lg:h-80 overflow-hidden">
          <img 
            src={siteImages.aboutBanner}
            alt="Guarantee"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-vybe-400 font-semibold text-sm tracking-wider uppercase mb-3 block">Our Promise</span>
              <h1 className="font-display text-4xl lg:text-5xl text-white mb-3">
                Our Guarantee
              </h1>
              <p className="text-slate-400 max-w-md mx-auto">
                Your satisfaction is our priority
              </p>
            </div>
          </div>
        </section>

        {/* Main Guarantee */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-br from-vybe-600 to-vybe-700 rounded-3xl p-8 lg:p-12 text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h2 className="font-display text-3xl lg:text-4xl text-white mb-4">
                30-Day Money-Back Guarantee
              </h2>
              <p className="text-vybe-100 text-lg max-w-2xl mx-auto leading-relaxed">
                We're confident you'll love our products. If you're not completely satisfied with your purchase, 
                return any unopened products within 30 days for a full refund. No questions asked.
              </p>
            </div>
          </div>
        </section>

        {/* What's Covered */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <h2 className="font-display text-3xl text-white mb-4">What Our Guarantee Covers</h2>
              <p className="text-slate-400 max-w-xl mx-auto">
                We stand behind every product we sell
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  icon: RotateCcw,
                  title: 'Easy Returns',
                  description: 'Return unopened products within 30 days for a full refund. Simple process, no hassle.',
                  color: 'vybe'
                },
                {
                  icon: Award,
                  title: 'Quality Assurance',
                  description: 'Every product is third-party tested. If you receive a defective product, we replace it free.',
                  color: 'cyan'
                },
                {
                  icon: Heart,
                  title: 'Customer Support',
                  description: 'Our team is here to help with any issues. Quick responses and friendly service.',
                  color: 'coral'
                }
              ].map((item, i) => (
                <div key={i} className="bg-slate-800/50 rounded-2xl p-6 border border-slate-700/50 text-center">
                  <div className={`w-14 h-14 bg-${item.color}-600/20 rounded-xl flex items-center justify-center mx-auto mb-5`}>
                    <item.icon className={`w-7 h-7 text-${item.color}-400`} />
                  </div>
                  <h3 className="font-bold text-white text-lg mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="font-display text-2xl text-white mb-4">How to Use Our Guarantee</h2>
            </div>

            <div className="space-y-4">
              {[
                { step: '1', title: 'Contact Us', desc: `Email ${CONTACT_INFO.email} or call ${CONTACT_INFO.phone} with your order number.` },
                { step: '2', title: 'Get Instructions', desc: 'We\'ll provide return shipping instructions and the return address.' },
                { step: '3', title: 'Ship Your Return', desc: 'Pack the unopened products securely and ship them back.' },
                { step: '4', title: 'Receive Refund', desc: 'Once received, your refund is processed within 5-7 business days.' }
              ].map((step, i) => (
                <div key={i} className="flex gap-5 p-5 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="w-12 h-12 bg-gradient-to-br from-vybe-600 to-vybe-500 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">{step.step}</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-white mb-1">{step.title}</h4>
                    <p className="text-slate-400 text-sm">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fine Print */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-slate-800/50 rounded-2xl p-6 lg:p-8 border border-slate-700/50">
              <h3 className="font-display text-xl text-white mb-5">Guarantee Details</h3>
              <ul className="space-y-3">
                {[
                  'Valid for 30 days from the date of delivery',
                  'Products must be unopened and in original packaging',
                  'Proof of purchase (order number or email confirmation) required',
                  'Return shipping costs are the customer\'s responsibility unless product is defective',
                  'Refunds are processed to the original payment method',
                  'Processing time: 5-7 business days after we receive the return'
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-400 text-sm">
                    <CheckCircle className="w-4 h-4 text-vybe-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-16 bg-slate-900">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Zap className="w-12 h-12 text-vybe-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl text-white mb-4">Have Questions?</h2>
            <p className="text-slate-400 mb-8">Our support team is here to help you.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href={`mailto:${CONTACT_INFO.email}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-vybe-600 text-white font-semibold rounded-xl hover:bg-vybe-500 transition-all"
              >
                <Mail className="w-5 h-5" />
                {CONTACT_INFO.email}
              </a>
              <a 
                href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-slate-700 text-white font-semibold rounded-xl hover:bg-slate-600 transition-all"
              >
                <Phone className="w-5 h-5" />
                {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-slate-950">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-2xl text-white mb-4">Shop with Confidence</h2>
            <p className="text-slate-400 mb-8">Your satisfaction is guaranteed. Start your keto journey today.</p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-vybe-600 to-vybe-500 text-white font-bold rounded-full hover:shadow-glow-purple transition-all"
            >
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default GuaranteePage;
